import { defineStore } from "pinia";
import type { ArtType, Artwork } from "~~/types/artwork";
import { useSearchHistory } from "./artworks.search";
import { useMessage } from "./app-toast";

export interface ArtworkDataMeta {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface ArtworkStoreState {
    artworksFetching: boolean;
    filtering: boolean;
    artworks: Artwork[];
    meta: ArtworkDataMeta | null;
    showFilters: boolean;
    filtersCount: number;
    showHistory: boolean;
    showSearch: boolean;


    page: number;
    limit: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    // serch
    search: string;
    // filter by art type
    artType: ArtType;
    color: string | null;
    isForSale: boolean;
}

export const useArtworkStore = defineStore("ArtworkStore", {
    state: (): ArtworkStoreState => ({
        artworksFetching: false,
        filtering: false,
        artworks: [],
        meta: null,
        showFilters: false,
        filtersCount: 0,

        page: 1,
        limit: 50,
        sortBy: "createdAt",
        sortOrder: "desc",

        search: "",
        artType: "",
        color: null,
        isForSale: false,
        showHistory: false,
        showSearch: false,

    }),

    actions: {
        async getAllArtworks(primary = true): Promise<void> {
            const { $api } = useNuxtApp()
            // const messageStore = useMessageStore();
            try {
                if (primary) {
                    this.artworksFetching = true;
                } else {
                    this.filtering = true;
                }

                const response = await $api<any>("/art/artworks", {
                    params: {
                        page: this.page,
                        limit: this.limit,
                        sortBy: this.sortBy,
                        sortOrder: this.sortOrder,
                        search: this.search,
                        artType: this.artType,
                        color: this.color,
                        isForSale: this.isForSale,
                    },
                });
                this.artworks = response.data;
                this.meta = response.meta;
                this.scrollTop();
            } catch (_e) {
                // messageStore.handleErrorMassage(e);
            } finally {
                this.artworksFetching = false;
                this.filtering = false;
            }
        },

        scrollTop() {
            const element = document.getElementById("grid-top");
            if (!element) return;
            setTimeout(() => {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                    inline: "nearest",
                });
            }, 100);
        },

        openHystory() {
            this.showHistory = true;
        },

        closeHystory() {
            this.showHistory = false;
        },

        openSearch() {
            document.body.style.overflow = "hidden";
            this.showSearch = true;
        },

        closeSearch() {
            document.body.style.overflow = "auto";
            setTimeout(() => {
                this.showSearch = false;
            }, 300);
        },

        openFilters() {
            this.showFilters = true;
            // document.body.style.overflow = "hidden";
        },
        closeFilters() {
            // document.body.style.overflow = "auto";
            this.showFilters = false;
        },
        toggleFilters() {
            if (this.showFilters) {
                this.closeFilters();
            } else {
                this.openFilters();
            }
        },
        setPage(page: number) {
            this.page = page;
            this.getAllArtworks();
        },

        setLimit(limit: number) {
            this.limit = limit;
        },

        setSort(sortBy: string, sortOrder: "asc" | "desc") {
            this.sortBy = sortBy;
            this.sortOrder = sortOrder;
            this.getAllArtworks();
        },

        async searchByHistory(item: string) {
            this.search = item;
            await this.getAllArtworks(false);
        },
        async searchArtwork() {
            const toast = useMessage()
            const history = useSearchHistory();
            if (!this.search) {
                toast.add("Please enter search term", "error");
                return;
            }
            history.saveHistoryItem(this.search);
            await this.getAllArtworks(false);
            this.closeSearch()
        },

        async clearSerch() {
            this.search = "";

            await this.getAllArtworks(false);
        },

        async filterByArtType(artType: ArtType) {
            this.page = 1
            if (this.artType === artType) {
                this.artType = "";
                this.filtersCount -= 1;
            } else {
                this.artType = artType;
                this.filtersCount += 1;
            }
            this.closeHystory();
            this.closeSearch()
            await this.getAllArtworks(false);
        },

        async filterByColor(color: string) {
            this.page = 1
            if (this.color === color) {
                this.filtersCount -= 1;
                this.color = null;
            } else {
                this.filtersCount += 1;
                this.color = color;
            }
            this.closeHystory();
            this.closeSearch()
            await this.getAllArtworks(false);
        },

        async filterByIsForSale() {
            this.page = 1
            this.isForSale = !this.isForSale;
            this.closeHystory();
            this.closeSearch()
            if (this.isForSale) {
                this.filtersCount += 1;
            } else {
                this.filtersCount -= 1;
            }
            await this.getAllArtworks(false);
        },

        async resetFilters() {
            this.page = 1;
            this.search = "";
            this.artType = "";
            this.color = null;
            this.isForSale = false;

            await this.getAllArtworks(false);
            this.filtersCount = 0;
        },
        removeUser(userId: string) {
            if (!userId) return;
            this.artworks = this.artworks.filter((a) => a._id !== userId);
        },
        async changePage(page: number) {
            if (!this.page || this.page === page) return;
            this.page = page;
            await this.getAllArtworks(false);
        },
        async nextPage() {
            if (!this.meta?.totalPages) return;
            if (this.page < this.meta.totalPages) {
                this.page = this.page += 1;
                await this.getAllArtworks(false);
            }
        },
        async prevPage() {
            if (!this.meta?.totalPages) return;
            if (this.page === 1) return;
            this.page = this.page -= 1;
            await this.getAllArtworks(false);
        },
    },
});
